import React, { useEffect, useRef, useState } from "react";
import Drawer from "~/components/Drawer/Drawer";
import { useMediaQuery } from "~/hooks/useMediaQuery";
import CloseIcon from "~/icons/CloseIcon";
import styles from "./StoreFinder.module.css";

type Props = {
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
};

const DEFAULT_TITLE = "Choose your store";
// Exit animation should feel quicker than the entrance.
const EXIT_MS = 180;

const StoreFinderShell: React.FC<Props> = ({
  visible,
  onClose,
  children,
  title = DEFAULT_TITLE,
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const panelRef = useRef<HTMLDivElement>(null);

  // Keep the desktop dialog mounted for a brief exit animation after `visible`
  // flips to false.
  const [mounted, setMounted] = useState(visible);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      setClosing(false);
      return;
    }
    if (!mounted) return;
    setClosing(true);
    const t = window.setTimeout(() => setMounted(false), EXIT_MS);
    return () => window.clearTimeout(t);
  }, [visible, mounted]);

  // Escape to close + focus the first interactive element on open (desktop;
  // the mobile Drawer handles its own focus/escape via headlessui Dialog).
  useEffect(() => {
    if (!visible || isMobile) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);

    const focusTarget = panelRef.current?.querySelector<HTMLElement>(
      "input, button, [tabindex]:not([tabindex='-1'])",
    );
    focusTarget?.focus();

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [visible, isMobile, onClose]);

  if (isMobile) {
    return (
      <Drawer visible={visible} onClose={onClose} title={title}>
        <div className={styles.shellSheet}>{children}</div>
      </Drawer>
    );
  }

  if (!mounted) return null;

  return (
    <div
      className={`${styles.shellBackdrop}${closing ? ` ${styles.shellClosing}` : ""}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        ref={panelRef}
        className={styles.shellModal}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className={styles.shellHeader}>
          <h2 className={styles.shellTitle}>{title}</h2>
          <button
            type="button"
            className={styles.shellClose}
            aria-label="Close"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>
        <div className={styles.shellBody}>{children}</div>
      </div>
    </div>
  );
};

export default StoreFinderShell;
